import React, { useState } from "react";
import { ClipboardList, Plus } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { GenericTable } from "@shared/components/GenericTable";
import {
  useSopTabDetails,
  useSkillOptions,
  useDeleteSopTask,
} from "../../../../hooks";
import { AddTaskModal, EditTaskModal } from "../modals/taskModal";
import { useToast } from "@shared/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";

interface TasksTabProps {
  sopDetails: any;
}

interface Task {
  name: string;
  time: string;
  downtime: string;
  skillId: number[];
  isPhotoReq: string | null;
  skillName: string[];
}

const TasksTab: React.FC<TasksTabProps> = ({ sopDetails }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number>(-1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<{
    index: number;
    task: Task;
  } | null>(null);

  const deleteTaskMutation = useDeleteSopTask();
  const { toast } = useToast();

  // Fetch task data using the hook
  const { data: taskData } = useSopTabDetails({
    id: sopDetails.id.toString(),
    remoteUtilityId: sopDetails.remoteUtilityId?.toString(),
    fieldName: "task",
  });

  // Get skill options for skill name mapping
  const { getSkillNameById } = useSkillOptions();

  // Event handlers
  const handleAddTask = () => {
    setIsAddModalOpen(true);
  };

  const handleEditTask = (taskIndex: number, task: Task) => {
    setSelectedTask(task);
    setSelectedTaskIndex(taskIndex);
    setIsEditModalOpen(true);
  };

  const handleDeleteTask = (taskIndex: number, task: Task) => {
    setTaskToDelete({ index: taskIndex, task });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      await deleteTaskMutation.mutateAsync({
        sopId: sopDetails.id.toString(),
        taskName: taskToDelete.task.name,
      });

      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedTask(null);
    setSelectedTaskIndex(-1);
  };

  // Extract tasks from API response and add index for row identification
  const tasks: (Task & { index: number })[] = (
    taskData?.result?.task || []
  ).map((task: Task, index: number) => ({
    ...task,
    index,
  }));

  // GenericTable column configuration
  const tableColumns = [
    {
      key: "index",
      header: "Sr. No",
      render: (value: number) => (
        <span className="font-medium">{value + 1}</span>
      ),
    },
    {
      key: "name",
      header: "Task",
      sortable: true,
      render: (value: string) => <span className="text-gray-900">{value}</span>,
    },
    {
      key: "time",
      header: "Time (Mins)",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-900">{value || "0"}</span>
      ),
    },
    {
      key: "downtime",
      header: "Downtime (Mins)",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-900">{value || "0"}</span>
      ),
    },
    {
      key: "skills",
      header: "Skills",
      render: (value: any, row: Task & { index: number }) => (
        <div className="flex flex-wrap gap-1">
          {row.skillName && row.skillName.length > 0 ? (
            row.skillName.map((skillName, idx) => (
              <Badge
                key={idx}
                variant="outline"
                className="bg-green-50 border-green-200"
              >
                {skillName}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </div>
      ),
    },
    {
      key: "isPhotoReq",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) =>
          value === "Yes" ? "" : "bg-secondary",
        getVariant: (value: string) =>
          value === "Yes" ? "default" : "secondary",
      },
      render: (value: string) =>
        value === "Yes" ? "Yes" : value === "No" ? "No" : "Not Set",
    },
  ];

  // GenericTable actions configuration
  const tableActions = [
    {
      label: "Edit Task",
      onClick: (row: Task & { index: number }) =>
        handleEditTask(row.index, row),
      icon: "Pencil" as const,
      variant: "ghost" as const,
    },
    {
      label: "Delete Task",
      onClick: (row: Task & { index: number }) =>
        handleDeleteTask(row.index, row),
      icon: "Trash2" as const,
      variant: "ghost" as const,
      className: "text-destructive hover:text-destructive",
    },
  ];

  return (
    <>
      <div className="mt-4 space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center pb-2">
                <ClipboardList className="mr-2 h-5 w-5 text-primary" />
                Task Sequence
              </CardTitle>
              <CardDescription>Step-by-step tasks for this SOP</CardDescription>
            </div>
            <Button onClick={handleAddTask}>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            <GenericTable
              data={tasks}
              columns={tableColumns}
              rowKey="index"
              actions={tableActions}
              actionsType="dropdown"
              emptyMessage="No tasks defined for this SOP"
            />
          </CardContent>
        </Card>
      </div>

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        sopId={sopDetails.id.toString()}
      />

      {/* Edit Task Modal */}
      <EditTaskModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        sopId={sopDetails.id.toString()}
        task={selectedTask}
        taskIndex={selectedTaskIndex}
      />
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the task "
              {taskToDelete?.task.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTask}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTaskMutation.isPending}
            >
              {deleteTaskMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TasksTab;
